// Humanoid visual sync — the ONLY module in src/physics that imports `three`.
// Builds a per-humanoid mesh group matching its colliders and syncs it each
// frame from a flat TransformBuffer via `offsetOf` (never a hand-computed
// index*7 — bodies despawn between scenarios in the humanoid lab).
import * as THREE from 'three';
import type { TransformBuffer } from 'box3d-web';
import {
  HUMANOID_BONE_NAMES,
  HUMANOID_JOINT_DEFS,
  HUMANOID_REFERENCE_HEIGHT_M,
  scaledBoneShapeDims,
  type BoneShapeDims,
  type HumanoidBoneName,
  type HumanoidJointDef,
} from './humanoidBones';
import type { Humanoid } from './humanoid';
import type { StaticBoxGeometry } from './humanoidLabPhysics';
import type { MuscleGroupName } from './humanoidMuscleController';

export type HumanoidVisualStyle = 'mannequin' | 'collider';

const MANNEQUIN_COLOR = 0x8a8f98;
const JOINT_ACCENT_COLOR = 0x5c6169;
const STATIC_GEOMETRY_COLOR = 0x565c66;

/** Cool (low) → hot (high) activation palette endpoints. */
const ACT_COOL = new THREE.Color(0x2a4a7a);
const ACT_MID = new THREE.Color(0xc4a24a);
const ACT_HOT = new THREE.Color(0xff4a2a);
const ACT_BASE = new THREE.Color(MANNEQUIN_COLOR);

/**
 * Map muscle groups → primary body meshes that should light up.
 * Shared bones take the max of contributing groups each frame.
 */
const MUSCLE_GROUP_BONES: Readonly<Record<MuscleGroupName, readonly HumanoidBoneName[]>> = {
  leftAnkle: ['leftFoot', 'leftLowerLeg'],
  rightAnkle: ['rightFoot', 'rightLowerLeg'],
  leftKnee: ['leftLowerLeg', 'leftUpperLeg'],
  rightKnee: ['rightLowerLeg', 'rightUpperLeg'],
  leftHip: ['leftUpperLeg', 'hips'],
  rightHip: ['rightUpperLeg', 'hips'],
  trunk: ['spine', 'hips'],
};

export interface HumanoidVisual {
  readonly group: THREE.Group;
  /** Re-reads every tracked bone's pose from `buffer` and applies it to the
   *  matching mesh via `buffer.offsetOf(body)`. */
  sync(buffer: TransformBuffer): void;
  /**
   * Cool-to-hot emissive/color tint from muscle group activations (0–1).
   * No-op for collider/wireframe style. Pass null/empty to restore base look.
   */
  setMuscleActivation(groupActivation: Readonly<Partial<Record<MuscleGroupName, number>>> | null): void;
  /**
   * Presentation-only: bright emissive accent on the swinging foot during
   * one-leg hold. Call after `setMuscleActivation` each frame; pass null to
   * leave materials alone (activation pass restores base look).
   */
  setSwingFootHighlight(side: 'left' | 'right' | null): void;
  dispose(): void;
}

/** Bright gold accent for swing-foot visibility (presentation only). */
const SWING_FOOT_TINT = new THREE.Color(0xffe066);
const SWING_FOOT_EMISSIVE = new THREE.Color(0xffcc22);

function geometryForDims(dims: BoneShapeDims): THREE.BufferGeometry {
  if (dims.kind === 'box') {
    const [hx, hy, hz] = dims.halfExtentsM;
    return new THREE.BoxGeometry(hx * 2, hy * 2, hz * 2);
  }
  if (dims.kind === 'sphere') {
    return new THREE.SphereGeometry(dims.radiusM, 20, 16);
  }
  // three's CapsuleGeometry `length` is the straight cylindrical section
  // (excluding the hemispherical caps) — exactly box3d-web's `halfHeightM`
  // convention (see humanoidBones.ts's CapsuleDims doc comment), so
  // `halfHeightM * 2` matches with no adjustment. Both default to a
  // Y-aligned capsule, matching box3d-web's addCapsule convention (the
  // T-pose layout in humanoid.ts pre-rotates arm bodies 90deg about Z to lay
  // them along +/-X, confirming the physics capsule itself is Y-aligned).
  return new THREE.CapsuleGeometry(dims.radiusM, dims.halfHeightM * 2, 4, 10);
}

function jointAccentRadius(def: HumanoidJointDef, heightM: number): number {
  const scale = heightM / HUMANOID_REFERENCE_HEIGHT_M;
  return (def.type === 'spherical' ? 0.052 : 0.042) * scale;
}

function activationColor(t: number, target: THREE.Color): THREE.Color {
  const a = Math.max(0, Math.min(1, t));
  if (a < 0.02) {
    return target.copy(ACT_BASE);
  }
  if (a < 0.5) {
    return target.copy(ACT_COOL).lerp(ACT_MID, a / 0.5);
  }
  return target.copy(ACT_MID).lerp(ACT_HOT, (a - 0.5) / 0.5);
}

/**
 * Collapse group activations into per-bone levels (max of contributors).
 * Exported for unit tests / HUD adapters if needed.
 */
export function boneActivationFromGroups(
  groupActivation: Readonly<Partial<Record<MuscleGroupName, number>>> | null | undefined,
): Record<HumanoidBoneName, number> {
  const out = {} as Record<HumanoidBoneName, number>;
  for (const bone of HUMANOID_BONE_NAMES) out[bone] = 0;
  if (!groupActivation) return out;
  for (const group of Object.keys(MUSCLE_GROUP_BONES) as MuscleGroupName[]) {
    const level = groupActivation[group] ?? 0;
    if (!(level > 0)) continue;
    for (const bone of MUSCLE_GROUP_BONES[group]) {
      out[bone] = Math.max(out[bone], Math.max(0, Math.min(1, level)));
    }
  }
  return out;
}

export function createHumanoidVisual(humanoid: Humanoid, style: HumanoidVisualStyle = 'mannequin'): HumanoidVisual {
  const group = new THREE.Group();
  const meshes = {} as Record<HumanoidBoneName, THREE.Mesh>;
  const bodyMaterials = {} as Record<HumanoidBoneName, THREE.MeshStandardMaterial | null>;
  const disposables: Array<{ dispose(): void }> = [];
  const tintScratch = new THREE.Color();

  const sharedColliderMaterial =
    style === 'collider'
      ? new THREE.MeshBasicMaterial({ color: MANNEQUIN_COLOR, wireframe: true, transparent: true, opacity: 0.55 })
      : null;
  if (sharedColliderMaterial) disposables.push(sharedColliderMaterial);

  const jointMaterial = new THREE.MeshStandardMaterial({ color: JOINT_ACCENT_COLOR, roughness: 0.6, metalness: 0.12 });
  disposables.push(jointMaterial);

  for (const bone of HUMANOID_BONE_NAMES) {
    // Re-derive the same scaled dims createHumanoid used to size the physics
    // shapes, so the mesh always matches the collider under height/proportion
    // overrides.
    const dims = scaledBoneShapeDims(
      bone,
      humanoid.spec.height,
      humanoid.spec.proportionPreset,
      humanoid.spec.footLayout,
    );
    const geometry = geometryForDims(dims);
    disposables.push(geometry);

    let material: THREE.Material;
    if (style === 'mannequin') {
      // Per-bone materials so muscle activation can tint regions independently.
      const mat = new THREE.MeshStandardMaterial({
        color: MANNEQUIN_COLOR,
        roughness: 0.78,
        metalness: 0.06,
        emissive: 0x000000,
        emissiveIntensity: 0,
      });
      disposables.push(mat);
      bodyMaterials[bone] = mat;
      material = mat;
    } else {
      bodyMaterials[bone] = null;
      material = sharedColliderMaterial!;
    }

    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    // Tap-to-shove (Alive mode, HumanoidLab.tsx) identifies which bone a
    // pointer raycast hit via this tag — additive, no interface change.
    mesh.userData.bone = bone;
    meshes[bone] = mesh;
    group.add(mesh);
  }

  let jointAccentsBuilt = false;

  function buildJointAccents(buffer: TransformBuffer): void {
    if (jointAccentsBuilt || style !== 'mannequin') return;
    jointAccentsBuilt = true;
    const transforms = buffer.transforms;
    for (const def of HUMANOID_JOINT_DEFS) {
      const childMesh = meshes[def.child];
      const childOffset = buffer.offsetOf(humanoid.bodies[def.child]);
      if (!childMesh || childOffset === undefined) continue;

      const childPos = new THREE.Vector3(transforms[childOffset], transforms[childOffset + 1], transforms[childOffset + 2]);
      const childQuat = new THREE.Quaternion(
        transforms[childOffset + 3],
        transforms[childOffset + 4],
        transforms[childOffset + 5],
        transforms[childOffset + 6],
      );
      const anchorWorld = new THREE.Vector3(...humanoid.jointAnchors[def.name]);
      // Local anchor offset in the child's own frame, computed once at the
      // child's SPAWN pose. Parenting the accent dot to the child mesh below
      // then keeps it glued to that anatomical point for free — every later
      // frame's `sync()` only has to move the child mesh itself.
      const localAnchor = anchorWorld.clone().sub(childPos).applyQuaternion(childQuat.clone().invert());

      const geometry = new THREE.SphereGeometry(jointAccentRadius(def, humanoid.spec.height), 8, 6);
      disposables.push(geometry);
      const dot = new THREE.Mesh(geometry, jointMaterial);
      dot.position.copy(localAnchor);
      childMesh.add(dot);
    }
  }

  function sync(buffer: TransformBuffer): void {
    const transforms = buffer.transforms;
    for (const bone of HUMANOID_BONE_NAMES) {
      const mesh = meshes[bone];
      const offset = buffer.offsetOf(humanoid.bodies[bone]);
      if (!mesh || offset === undefined) continue;
      mesh.position.set(transforms[offset], transforms[offset + 1], transforms[offset + 2]);
      mesh.quaternion.set(transforms[offset + 3], transforms[offset + 4], transforms[offset + 5], transforms[offset + 6]);
    }
    buildJointAccents(buffer);
  }

  function setMuscleActivation(
    groupActivation: Readonly<Partial<Record<MuscleGroupName, number>>> | null,
  ): void {
    if (style !== 'mannequin') return;
    const levels = boneActivationFromGroups(groupActivation);
    for (const bone of HUMANOID_BONE_NAMES) {
      const mat = bodyMaterials[bone];
      if (!mat) continue;
      const a = levels[bone];
      activationColor(a, tintScratch);
      mat.color.copy(tintScratch);
      if (a < 0.02) {
        mat.emissive.setHex(0x000000);
        mat.emissiveIntensity = 0;
      } else {
        mat.emissive.copy(tintScratch);
        mat.emissiveIntensity = 0.25 + a * 0.95;
      }
    }
  }

  function setSwingFootHighlight(side: 'left' | 'right' | null): void {
    if (style !== 'mannequin' || side === null) return;
    const bone: HumanoidBoneName = side === 'left' ? 'leftFoot' : 'rightFoot';
    const mat = bodyMaterials[bone];
    if (!mat) return;
    // Override activation tint with a high-contrast gold so the airborne foot
    // reads clearly on the mannequin (presentation only; no extra meshes).
    mat.color.copy(SWING_FOOT_TINT);
    mat.emissive.copy(SWING_FOOT_EMISSIVE);
    mat.emissiveIntensity = 1.35;
  }

  function dispose(): void {
    for (const item of disposables) item.dispose();
  }

  return { group, sync, setMuscleActivation, setSwingFootHighlight, dispose };
}

// ---------------------------------------------------------------------------
// Muscle-balance COM / support markers (renderer-only; no physics coupling)
// ---------------------------------------------------------------------------

export interface MuscleBalanceMarkers {
  readonly group: THREE.Group;
  update(
    centerOfMass: readonly [number, number, number],
    supportCenter: readonly [number, number, number],
    supportErrorM: number,
  ): void;
  setVisible(visible: boolean): void;
  dispose(): void;
}

/**
 * Readable balance cues: COM sphere, support-center disc, and a connector
 * whose color/thickness scales with horizontal support error.
 */
export function createMuscleBalanceMarkers(): MuscleBalanceMarkers {
  const group = new THREE.Group();
  group.visible = false;
  const disposables: Array<{ dispose(): void }> = [];

  const comGeom = new THREE.SphereGeometry(0.055, 16, 12);
  const comMat = new THREE.MeshStandardMaterial({
    color: 0xffd56a,
    emissive: 0xaa7720,
    emissiveIntensity: 0.55,
    roughness: 0.4,
    metalness: 0.1,
  });
  disposables.push(comGeom, comMat);
  const comMesh = new THREE.Mesh(comGeom, comMat);
  comMesh.renderOrder = 2;
  group.add(comMesh);

  const supportGeom = new THREE.CylinderGeometry(0.09, 0.09, 0.02, 20);
  const supportMat = new THREE.MeshStandardMaterial({
    color: 0x5ec8ff,
    emissive: 0x1a6a9a,
    emissiveIntensity: 0.45,
    roughness: 0.55,
    metalness: 0.05,
  });
  disposables.push(supportGeom, supportMat);
  const supportMesh = new THREE.Mesh(supportGeom, supportMat);
  supportMesh.renderOrder = 2;
  group.add(supportMesh);

  // Thin vertical stem on support so it reads on the ground plane.
  const stemGeom = new THREE.CylinderGeometry(0.012, 0.012, 0.12, 8);
  const stemMat = new THREE.MeshStandardMaterial({
    color: 0x5ec8ff,
    emissive: 0x1a6a9a,
    emissiveIntensity: 0.35,
    roughness: 0.6,
  });
  disposables.push(stemGeom, stemMat);
  const stemMesh = new THREE.Mesh(stemGeom, stemMat);
  group.add(stemMesh);

  const lineGeom = new THREE.BufferGeometry();
  const linePositions = new Float32Array(6);
  lineGeom.setAttribute('position', new THREE.BufferAttribute(linePositions, 3));
  const lineMat = new THREE.LineBasicMaterial({ color: 0x8ef0a0, linewidth: 1 });
  disposables.push(lineGeom, lineMat);
  const errorLine = new THREE.Line(lineGeom, lineMat);
  errorLine.renderOrder = 3;
  group.add(errorLine);

  // Horizontal ground projection of COM (helps read lateral error).
  const projGeom = new THREE.RingGeometry(0.03, 0.05, 20);
  const projMat = new THREE.MeshBasicMaterial({
    color: 0xffd56a,
    transparent: true,
    opacity: 0.75,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
  disposables.push(projGeom, projMat);
  const projMesh = new THREE.Mesh(projGeom, projMat);
  projMesh.rotation.x = -Math.PI / 2;
  group.add(projMesh);

  const lineColorGood = new THREE.Color(0x6ef59a);
  const lineColorBad = new THREE.Color(0xff6b4a);
  const lineMix = new THREE.Color();

  function update(
    centerOfMass: readonly [number, number, number],
    supportCenter: readonly [number, number, number],
    supportErrorM: number,
  ): void {
    const [cx, cy, cz] = centerOfMass;
    const [sx, sy, sz] = supportCenter;
    comMesh.position.set(cx, cy, cz);
    // Support marker sits slightly above ground if support Y is near zero.
    const supportY = Math.max(0.01, sy);
    supportMesh.position.set(sx, supportY, sz);
    stemMesh.position.set(sx, supportY + 0.06, sz);
    projMesh.position.set(cx, 0.015, cz);

    linePositions[0] = cx;
    linePositions[1] = cy;
    linePositions[2] = cz;
    linePositions[3] = sx;
    linePositions[4] = supportY;
    linePositions[5] = sz;
    lineGeom.attributes.position.needsUpdate = true;
    lineGeom.computeBoundingSphere();

    // Error cue: green when COM is over feet, red as horizontal error grows.
    const err = Math.max(0, supportErrorM);
    const t = Math.max(0, Math.min(1, err / 0.12));
    lineMix.copy(lineColorGood).lerp(lineColorBad, t);
    lineMat.color.copy(lineMix);
    // Slight scale pulse on support disc when error is large.
    const scale = 1 + t * 0.55;
    supportMesh.scale.set(scale, 1, scale);
  }

  function setVisible(visible: boolean): void {
    group.visible = visible;
  }

  function dispose(): void {
    for (const item of disposables) item.dispose();
  }

  return { group, update, setVisible, dispose };
}

export interface StaticSceneryVisual {
  readonly group: THREE.Group;
  dispose(): void;
}

/**
 * Static scenario geometry (e.g. the stairs scenario's 5 boxes) never moves,
 * so unlike `createHumanoidVisual` this has no per-frame `sync` — build the
 * meshes once from the runtime's exposed `StaticBoxGeometry[]` when the
 * scenario activates and dispose them on scenario switch.
 */
export function createStaticBoxesVisual(boxes: readonly StaticBoxGeometry[]): StaticSceneryVisual {
  const group = new THREE.Group();
  const disposables: Array<{ dispose(): void }> = [];

  const material = new THREE.MeshStandardMaterial({ color: STATIC_GEOMETRY_COLOR, roughness: 0.9, metalness: 0.04 });
  disposables.push(material);

  for (const box of boxes) {
    const [hx, hy, hz] = box.halfExtentsM;
    const geometry = new THREE.BoxGeometry(hx * 2, hy * 2, hz * 2);
    disposables.push(geometry);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(box.position[0], box.position[1], box.position[2]);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
  }

  function dispose(): void {
    for (const item of disposables) item.dispose();
  }

  return { group, dispose };
}
